import { Send } from "lucide-react";
import { useState } from "react";
import type { WholesaleEmailView } from "../../../shared/contracts.js";
import { mutationErrorMessage } from "../../api/caseMutations.js";
import { useSendWholesaleEmail, useWholesaleEmailPreview } from "../../api/wholesaleQueries.js";
import { AsyncPanel } from "../../ui/AsyncPanel.js";
import { Button } from "../../ui/Button.js";
import { Dialog, DialogActions } from "../../ui/Dialog.js";

/**
 * The manual customer email (spec §5): opening the dialog reads the
 * preview the server would enqueue — the address, the sender, the subject
 * and the plain text with the WS number, the reference and the parts that
 * are in — and only **Send** enqueues it, exactly once per click, through
 * the outbox. Nothing is ever sent automatically; sending again later is
 * allowed (more parts may have arrived) and each send is its own row on
 * the order.
 *
 * The preview is read afresh every time the dialog opens, so it always
 * describes the parts verified so far. A 409 (no address, nothing arrived,
 * cancelled) or a network failure is shown in the dialog in the server's
 * words; nothing was sent.
 */

export interface EmailCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  number: string;
  customerName: string;
  /** Called with the outbox row once the server has queued the email. */
  onSent: (email: WholesaleEmailView) => void;
}

export function EmailCustomerDialog(props: EmailCustomerDialogProps) {
  if (!props.open) return null;
  return <EmailCustomerFlow {...props} />;
}

function EmailCustomerFlow({ onClose, orderId, number, customerName, onSent }: EmailCustomerDialogProps) {
  const preview = useWholesaleEmailPreview(orderId, true);
  const send = useSendWholesaleEmail(orderId);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (send.isPending) return;
    setError(null);
    send.mutate(undefined, {
      onSuccess: (email) => {
        onSent(email);
        onClose();
      },
      onError: (failure) => setError(mutationErrorMessage(failure)),
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Email customer · ${number}`}
      description={`Tells ${customerName} which parts on this order are in and ready for pickup or delivery. Nothing is sent until you choose Send.`}
      pending={send.isPending}
      size="large"
    >
      <AsyncPanel
        isLoading={preview.isPending}
        error={preview.error}
        onRetry={() => {
          void preview.refetch();
        }}
        loadingLabel="Preparing the email…"
        headingLevel={3}
      >
        {preview.data ? (
          <div className="email-preview">
            <dl className="email-preview__facts">
              <div className="email-preview__fact">
                <dt>To</dt>
                <dd>{preview.data.to ?? "No email on file"}</dd>
              </div>
              <div className="email-preview__fact">
                <dt>From</dt>
                <dd>{preview.data.from}</dd>
              </div>
              <div className="email-preview__fact">
                <dt>Subject</dt>
                <dd>{preview.data.subject}</dd>
              </div>
            </dl>
            <pre className="email-preview__text" aria-label="Email text">
              {preview.data.text}
            </pre>
          </div>
        ) : null}
      </AsyncPanel>

      {error ? (
        <p role="alert" className="form-alert">
          {error}
        </p>
      ) : null}

      <DialogActions>
        <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          icon={<Send />}
          busy={send.isPending}
          disabled={!preview.data || preview.data.to === null}
          onClick={submit}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
